import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import {
  type ConnectionCheckOutcome,
  getSaveDecision,
  isReportableConnectionCheckError,
  outcomeFromCheck,
  outcomeFromError,
} from './outcome';
import { type ConnectionCheck } from './types';

/** What a form shows about its check right now. */
export type ConnectionCheckDisplayStatus =
  | ConnectionCheckOutcome['kind']
  | 'idle'
  | 'running';

/**
 * `blocked`: the save waits for a second click on a failure it just showed.
 * `skipped`: a save is already guarded, or the values changed while checking.
 */
export type GuardedSaveResult = 'blocked' | 'saved' | 'skipped';

type PendingRun = {
  readonly controller: AbortController;
  readonly key: string;
  readonly promise: Promise<ConnectionCheckOutcome | undefined>;
};

export type ConnectionCheckStateOptions<TRequest> = {
  /** Runs a check and answers once it no longer runs. */
  readonly checkConnection: (input: {
    readonly request: TRequest;
    readonly signal: AbortSignal;
  }) => Promise<ConnectionCheck>;
  /** Says that the values changed while they were being checked. */
  readonly notifyChangedWhileChecking: () => void;
  /** Says that a save went through although its check could not run. */
  readonly notifySavedUnchecked: () => void;
  /** Reports a check that broke on our side, e.g. to the app's log. */
  readonly onError?: (error: unknown) => void;
  /** Sees each outcome together with the request it was run for. */
  readonly onOutcome?: (
    outcome: ConnectionCheckOutcome,
    request: TRequest,
  ) => void;
  /** What to check with the values as they are now, or undefined while there is nothing to check. */
  readonly request: TRequest | undefined;
  /** The stored configuration's request. A save that sends exactly that needs no check. */
  readonly savedRequest?: TRequest;
};

const toKey = (request: unknown) =>
  request === undefined ? undefined : JSON.stringify(request);

/**
 * Checks the connection a form describes and gates its save on the outcome.
 * An outcome belongs to the exact request it was run for, so values edited
 * since are checked again before they may be saved.
 *
 * The texts, the transport and the logging stay with the app, which passes
 * them in; what stays here is when a check runs, what it answers for and
 * whether a save may go ahead.
 */
export const useConnectionCheckState = <TRequest>({
  checkConnection,
  notifyChangedWhileChecking,
  notifySavedUnchecked,
  onError,
  onOutcome,
  request,
  savedRequest,
}: ConnectionCheckStateOptions<TRequest>) => {
  const key = toKey(request);
  const savedKey = toKey(savedRequest);

  const [settled, setSettled] = useState<{
    readonly key: string;
    readonly outcome: ConnectionCheckOutcome;
  }>();
  const [runningKey, setRunningKey] = useState<string>();
  const [isGuardingSave, setIsGuardingSave] = useState(false);
  const [blockedKey, setBlockedKey] = useState<string>();

  const outcome = settled?.key === key ? settled?.outcome : undefined;

  // Read by callbacks started before a re-render; updated before effects run.
  const latestRef = useRef({
    checkConnection,
    key,
    notifyChangedWhileChecking,
    notifySavedUnchecked,
    onError,
    onOutcome,
    outcome,
    request,
    savedKey,
  });
  const pendingRef = useRef<PendingRun>(undefined);
  const isGuardingRef = useRef(false);

  useLayoutEffect(() => {
    latestRef.current = {
      checkConnection,
      key,
      notifyChangedWhileChecking,
      notifySavedUnchecked,
      onError,
      onOutcome,
      outcome,
      request,
      savedKey,
    };
  });

  useEffect(
    () => () => {
      pendingRef.current?.controller.abort();
    },
    [],
  );

  const run = useCallback(async () => {
    const {
      checkConnection: check,
      key: requestKey,
      onError: report,
      onOutcome: seeOutcome,
      request: toCheck,
    } = latestRef.current;

    if (requestKey === undefined || toCheck === undefined) {
      return undefined;
    }

    if (
      pendingRef.current?.key === requestKey &&
      !pendingRef.current.controller.signal.aborted
    ) {
      return pendingRef.current.promise;
    }

    // A check for values that are no longer in the form is not wanted anymore.
    pendingRef.current?.controller.abort();

    const controller = new AbortController();
    const promise = check({ request: toCheck, signal: controller.signal })
      .then(outcomeFromCheck, (error: unknown) => {
        if (controller.signal.aborted) {
          return undefined;
        }

        if (isReportableConnectionCheckError(error)) {
          report?.(error);
        }

        return outcomeFromError(error);
      })
      .then((result) => {
        if (pendingRef.current?.controller === controller) {
          pendingRef.current = undefined;
          setRunningKey(undefined);
        }

        if (result !== undefined) {
          setSettled({ key: requestKey, outcome: result });
          seeOutcome?.(result, toCheck);
        }

        return result;
      });

    pendingRef.current = { controller, key: requestKey, promise };
    setRunningKey(requestKey);

    return promise;
  }, []);

  /**
   * Saves once the current values have an outcome that allows it, checking
   * them first when they have none yet.
   */
  const guardSave = useCallback(
    async (save: () => unknown): Promise<GuardedSaveResult> => {
      if (isGuardingRef.current) {
        return 'skipped';
      }

      const {
        key: checkedKey,
        notifyChangedWhileChecking: sayChanged,
        notifySavedUnchecked: sayUnchecked,
        outcome: shownOutcome,
        savedKey: storedKey,
      } = latestRef.current;

      if (checkedKey === undefined || checkedKey === storedKey) {
        await save();

        return 'saved';
      }

      let decisive = shownOutcome;

      if (decisive === undefined) {
        isGuardingRef.current = true;
        setIsGuardingSave(true);

        try {
          decisive = await run();
        } finally {
          isGuardingRef.current = false;
          setIsGuardingSave(false);
        }

        if (decisive === undefined) {
          return 'skipped';
        }

        if (latestRef.current.key !== checkedKey) {
          sayChanged();

          return 'skipped';
        }
      }

      const decision = getSaveDecision(decisive, {
        wasShownBeforeSaving: shownOutcome !== undefined,
      });

      if (decision === 'block') {
        setBlockedKey(checkedKey);

        return 'blocked';
      }

      if (decision === 'saveUnchecked') {
        sayUnchecked();
      }

      await save();

      return 'saved';
    },
    [run],
  );

  const status: ConnectionCheckDisplayStatus =
    runningKey !== undefined && runningKey === key
      ? 'running'
      : (outcome?.kind ?? 'idle');

  return {
    /** There are values to check. */
    canRun: key !== undefined,
    guardSave,
    /** A save is waiting for the check it started. */
    isGuardingSave,
    /** The shown outcome failed, so saving now means saving anyway. */
    isSaveAnyway: outcome?.kind === 'failed',
    /** A save was held back because of the failure that is shown. */
    isSaveBlocked: outcome?.kind === 'failed' && blockedKey === key,
    outcome,
    run,
    status,
  };
};
